import { ASSET_SCHEMA_VERSION, CONTRACT_SCHEMA_VERSION, adapterFromContract, contractSchemaVersionOf, deserialiseAssetManifest, deserialiseChunk, deserialiseContract, deserialiseManifest, resolveChunk, type AssetManifest, type CompiledChunk, type DaedalusGameAdapter, type StandInArt, type WorldManifest } from "daedalus-core";
import { ODYSSEY_ADAPTER, isWorldVersion } from "odyssey-shared";
import { readCharacterManifest, standInArtFrom, type CharacterManifestRead } from "./stand-in-art.ts";
import { LIVE_EPOCH_HEADER, LIVE_SEQ_HEADER, livePointOf, type LivePoint } from "./live/live-point.ts";

export { LIVE_EPOCH_HEADER, LIVE_SEQ_HEADER, livePointOf, type LivePoint };

/**
 * Fetches a published world's pointer, manifest, asset manifest and chunks over HTTP. Chunk files arrive
 * brotli-encoded with `Content-Encoding: br`, so the browser decodes them before `json()`.
 */

/**
 * Everything the editor needs before drawing. `assetManifest` is the path, relative to `base`, of the
 * asset manifest `assets` was read from, which its pages sit beside: the world's own for the game, the
 * catalogue's for the editor (ADR-057 amendment).
 */
export interface LoadedWorld {
  readonly base: string;
  readonly version: string;
  readonly manifest: WorldManifest;
  readonly assetManifest: string;
  readonly assets: AssetManifest;
}

/**
 * Fetches a URL, naming it when the request itself fails, since the browser's own error for a request that
 * never got an answer ("Failed to fetch") says nothing of what was asked for.
 *
 * @param url URL.
 * @param init Request options.
 * @param fetcher The fetch to use.
 * @returns The response, whatever its status.
 * @throws Error naming the URL when no response came.
 */
export async function fetchNamed(url: string, init: RequestInit = {}, fetcher: typeof fetch = fetch): Promise<Response> {
  try {
    return await fetcher(url, init);
  } catch (error) {
    throw new Error(`${url} did not answer (${error instanceof Error ? error.message : String(error)})`);
  }
}

/**
 * Fetches text, failing loudly on any non-2xx status.
 *
 * @param url URL.
 * @param cache Fetch cache mode; the world pointer asks for `no-store` so a publish is seen at once.
 * @returns Body text.
 * @throws Error naming the URL and status, or the URL when no response came.
 */
async function fetchText(url: string, cache: RequestCache = "default"): Promise<string> {
  const response = await fetchNamed(url, { cache });
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return response.text();
}

/**
 * The files `loadWorld` reads, in order, each reported as it arrives: the pointer, the world manifest and
 * the asset manifest.
 */
export type WorldFile = "pointer" | "manifest" | "assets";

/**
 * Loads a version of a world from its pointer (ADR-050): `published.json` is what a live map server follows,
 * so the game built for Pages reads that and stays on the version the server runs; `current.json` is the
 * newest compile, which the editor reads and which the game reads on the dev server, whose map server
 * follows it under `dev:game` (ADR-050 amendment of 2026-09-26). A world with no published pointer falls back
 * to the current one, so a freshly built world still loads.
 *
 * @param base URL prefix that mirrors the R2 root (for the dev server, `/dist`).
 * @param worldId World id.
 * @param pointer Which pointer to follow.
 * @param onFile Told of each file as it arrives and reads, as the game's loading screen counts them.
 * @returns Manifests and the resolved version.
 * @throws Error when the pointer, the manifest or the asset manifest is missing or malformed.
 */
export async function loadWorld(base: string, worldId: string, pointer: "current" | "published" = "current", onFile: (file: WorldFile) => void = () => undefined): Promise<LoadedWorld> {
  const text = await pointerText(base, worldId, pointer);
  const current = JSON.parse(text) as { version: string; manifest: string };
  onFile("pointer");
  return loadManifests(base, current.version, current.manifest, onFile);
}

/**
 * Loads one named version of a world without reading a pointer: the version the game's map server says it runs
 * (ADR-050's amendment of 2026-10-02), whose manifest every compile writes at `worlds/<id>/<version>/manifest.json`.
 * A published version's files never change, so this loads the same world whenever it is asked.
 *
 * @param base URL prefix that mirrors the R2 root.
 * @param worldId World id.
 * @param version The version, a safe version name (`isWorldVersion`).
 * @param onFile Told of each file as it arrives and reads; `pointer` is told first, since no pointer is read.
 * @returns Manifests and the version.
 * @throws Error when the version is not a safe version name, or the manifest or the asset manifest is missing or
 *   malformed.
 */
export async function loadWorldVersion(base: string, worldId: string, version: string, onFile: (file: WorldFile) => void = () => undefined): Promise<LoadedWorld> {
  if (!isWorldVersion(version) || !isWorldVersion(worldId)) {
    throw new Error(`world ${worldId} version ${version} is not a version name`);
  }
  onFile("pointer");
  return loadManifests(base, version, `worlds/${worldId}/${version}/manifest.json`, onFile);
}

/**
 * Loads a world for the editor from its pointer: the pointer and the world manifest only. The asset manifest is not
 * read; the world's frames arrive from the library as the document names them (ADR-130 decision 2.6), so `assets`
 * starts as an empty manifest that the tab's pages fill in place.
 *
 * @param base URL prefix that mirrors the R2 root (for the dev server, `/dist`).
 * @param worldId World id.
 * @param onFile Told of each file as it arrives.
 * @returns The manifest, the version and an empty asset manifest.
 * @throws Error when the pointer or the manifest is missing or malformed.
 */
export async function loadEditorWorld(base: string, worldId: string, onFile: (file: WorldFile) => void = () => undefined): Promise<LoadedWorld> {
  const current = JSON.parse(await pointerText(base, worldId, "current")) as { version: string; manifest: string };
  onFile("pointer");
  const manifest = deserialiseManifest(await fetchText(`${base}/${current.manifest}`));
  onFile("manifest");
  return { base, version: current.version, manifest, assetManifest: manifest.assetManifest, assets: { schemaVersion: ASSET_SCHEMA_VERSION, pages: [], frames: {}, animations: {} } };
}

/**
 * The manifest and the asset manifest of a version.
 *
 * @param base URL prefix.
 * @param version The version.
 * @param manifestPath The manifest's path under the base.
 * @param onFile Told of each file as it arrives and reads.
 * @returns Manifests and the version.
 * @throws Error when either manifest is missing or malformed.
 */
async function loadManifests(base: string, version: string, manifestPath: string, onFile: (file: WorldFile) => void): Promise<LoadedWorld> {
  const manifest = deserialiseManifest(await fetchText(`${base}/${manifestPath}`));
  onFile("manifest");
  const assets = deserialiseAssetManifest(await fetchText(`${base}/${manifest.assetManifest}`));
  onFile("assets");
  return { base, version, manifest, assetManifest: manifest.assetManifest, assets };
}

/**
 * The version the world's published pointer names (ADR-050), which a live map server follows, for the top bar
 * and the status bar; the editor itself loads the newest compile, which may be another.
 *
 * @param world The loaded world, whose base and id locate the pointer.
 * @param fetcher The fetch to use.
 * @returns The version, or null when the world has never been published (no pointer).
 * @throws Error when the pointer answers anything but OK or not found, or names no version.
 */
export async function publishedVersion(world: LoadedWorld, fetcher: typeof fetch = fetch): Promise<string | null> {
  const url = `${world.base}/worlds/${world.manifest.worldId}/published.json`;
  const response = await fetcher(url, { cache: "no-store" });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(await response.text());
  } catch {
    throw new Error(`${url} is not JSON`);
  }
  const version = typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? (parsed as { version?: unknown }).version : undefined;
  if (typeof version !== "string" || version === "") {
    throw new Error(`${url} names no version`);
  }
  return version;
}

/**
 * The pointer document's text, falling back from `published.json` to `current.json`.
 *
 * @param base URL prefix.
 * @param worldId World id.
 * @param pointer Which pointer to follow.
 * @returns The document text.
 * @throws Error when neither pointer is there.
 */
async function pointerText(base: string, worldId: string, pointer: "current" | "published"): Promise<string> {
  if (pointer === "current") {
    return fetchText(`${base}/worlds/${worldId}/current.json`, "no-store");
  }
  try {
    return await fetchText(`${base}/worlds/${worldId}/published.json`, "no-store");
  } catch {
    return fetchText(`${base}/worlds/${worldId}/current.json`, "no-store");
  }
}

/**
 * Loads and validates one client chunk.
 *
 * @param world Loaded world.
 * @param mapId Map id.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns The chunk.
 * @throws Error when the chunk is outside the map, the fetch fails or the file is malformed.
 */
export async function loadClientChunk(world: LoadedWorld, mapId: string, cx: number, cy: number): Promise<CompiledChunk> {
  const path = resolveChunk(world.manifest, mapId, cx, cy, "client");
  return deserialiseChunk(await fetchText(`${world.base}/worlds/${world.manifest.worldId}/${world.version}/${path}`));
}

/**
 * Header the dev server names a served document's origin in, so a baseline served in place of a save is
 * not reported as one.
 */
const WORLD_SOURCE_HEADER = "X-World-Source";

/**
 * The files `loadEditorSources` reads, each reported as it arrives: the authoring document and the game
 * contract.
 */
export type EditorSourceFile = "document" | "contract";

/**
 * How many files the editor reads for a world before the library's frames: `loadEditorWorld`'s two and
 * `loadEditorSources`'s two, the count the loading screen's world step shows.
 */
export const EDITOR_WORLD_FILES = 4;

/**
 * Loads the authoring document and game contract the editor paints with: the document the dev server serves, which is the save when one exists (ADR-023) and the world's
 * baseline otherwise, falling back to the generated document beside the compiled package when the server
 * serves neither; the contract (ADR-052) from the dev server, null when nothing serves one; and, in
 * parallel with the rest, the character manifest, read once for the player stand-in's art (ADR-071 decision 1),
 * null with one console warning when the manifest cannot be used, and for the creature picker's looks.
 *
 * @param world Loaded world.
 * @param onFile Told of each file as it arrives, as the loading screen counts them.
 * @returns The texts, the stand-in's art, the character manifest as read, whether the document came from a save,
 *   the revision the server named it with (the build server's head sequence), null when it named none, and the
 *   open room's epoch and sequence the document was read at (ADR-086 decision 22), null when no room was open.
 * @throws Error when the fallback document is missing.
 */
export async function loadEditorSources(world: LoadedWorld, onFile: (file: EditorSourceFile) => void = () => undefined): Promise<{ documentText: string; contractText: string | null; standInArt: StandInArt | null; characters: CharacterManifestRead; saved: boolean; revision: number | null; live: LivePoint | null }> {
  const characters = readCharacterManifest(world.base);
  const worldId = world.manifest.worldId;
  const savedResponse = await fetch(`/api/worlds/${worldId}`);
  const saved = savedResponse.ok && savedResponse.headers.get(WORLD_SOURCE_HEADER) !== "baseline";
  const revision = savedResponse.ok ? revisionOf(savedResponse.headers.get("ETag")) : null;
  const live = savedResponse.ok ? livePointOf(savedResponse.headers.get(LIVE_EPOCH_HEADER), savedResponse.headers.get(LIVE_SEQ_HEADER)) : null;
  const documentText = savedResponse.ok ? await savedResponse.text() : await fetchText(`${world.base}/worlds/${worldId}/${world.version}/document.json`);
  onFile("document");
  const contractText = await loadContractText();
  onFile("contract");
  const read = await characters;
  return { documentText, contractText, standInArt: standInArtFrom(read, world.base, (message) => console.warn(message)), characters: read, saved, revision, live };
}

/**
 * The revision a served document's ETag names: the build server's head sequence, `"<n>"`. Cloudflare weakens an
 * ETag (`W/"<n>"`) when it compresses the answer on its way to the browser, so the weak form names the same
 * revision.
 *
 * @param etag The response's ETag, or null.
 * @returns The sequence, or null when the server names none (the dev server).
 */
export function revisionOf(etag: string | null): number | null {
  const match = etag === null ? null : /^(?:W\/)?"([1-9][0-9]{0,9})"$/.exec(etag);
  return match === null ? null : Number(match[1]);
}

/**
 * The served game contract's text (ADR-052). Only absence falls back: nothing answering the request and a
 * 404 both mean no contract is served, while any other status is a served contract the editor failed to
 * read and must not silently ignore.
 *
 * @returns The text, or null when no contract is served.
 * @throws Error naming the status when the dev server answers with anything but success or 404.
 */
async function loadContractText(): Promise<string | null> {
  let response: Response;
  try {
    response = await fetch("/api/contract", { cache: "no-store" });
  } catch {
    return null;
  }
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`/api/contract responded ${response.status}`);
  }
  return response.text();
}

/**
 * The adapter the editor edits with, and where it came from, by the rule `gameObjectTypes` applies for the
 * map server and the game client (ADR-052 amendment). A served contract supplies the game's declarations (entity
 * types, object types, collision brushes, footprint policy) over the built-in adapter's hooks, which are
 * code and are never served; with no contract the built-in adapter is used whole, so the editor runs
 * offline. A contract written to an older layout is old, not malformed: the editor falls back to the
 * built-in adapter and says so, since a world compiled before a layout change is exactly what the author is
 * about to recompile. Anything else is read as a contract of this build's layout, so a newer layout, text
 * that is not JSON or names no layout version, and a contract of this layout that does not parse are
 * errors, never a fallback; the boot reports them in the status bar.
 *
 * @param contractText The served contract's text, or null when none is served.
 * @returns The adapter and where its declarations came from.
 * @throws SyntaxError when the served text is not JSON; Error when it names no layout version, a newer
 *   layout than this build reads, or is of this build's layout and malformed.
 */
export function editorAdapter(contractText: string | null): { adapter: DaedalusGameAdapter; source: ContractSource } {
  if (contractText === null) {
    return { adapter: ODYSSEY_ADAPTER, source: "built-in" };
  }
  const version = contractSchemaVersionOf(contractText);
  if (version !== null && version < CONTRACT_SCHEMA_VERSION) {
    return { adapter: ODYSSEY_ADAPTER, source: "outdated" };
  }
  return { adapter: { ...ODYSSEY_ADAPTER, ...adapterFromContract(deserialiseContract(contractText)) }, source: "contract" };
}

/**
 * Where the editor's declarations came from: the served contract, the built-in adapter because none was
 * served, or the built-in adapter because the served one was written to an older layout than this build
 * reads.
 */
export type ContractSource = "contract" | "built-in" | "outdated";

/**
 * The URL of an atlas page, beside the asset manifest the world's atlas was read from.
 *
 * @param world Loaded world.
 * @param page Page index.
 * @returns The URL.
 * @throws Error when the asset manifest has no such page.
 */
export function pageUrl(world: LoadedWorld, page: number): string {
  const entry = world.assets.pages[page];
  if (entry === undefined) {
    throw new Error(`asset manifest has no page ${page}`);
  }
  const directory = world.assetManifest.slice(0, world.assetManifest.lastIndexOf("/"));
  return `${world.base}/${directory}/${entry.file}`;
}
